import { type RefObject, useRef } from 'react'

import { type PayrollProviderConfig } from 'utils/web_api.schemas'

interface ProviderStrategy {
  startConnection: (providerConfig: PayrollProviderConfig, supportsAutomatedDeductions?: boolean) => Promise<void>;
  openModal: () => void;
  isModalReady: () => boolean;
  cleanup: () => void;
}

interface UsePayrollProviderSelectionResult {
  payrollProviderSearchRef: RefObject<HTMLInputElement>;
  handleProviderSelection: (
    provider: PayrollProviderConfig,
    providerStrategies: Record<string, ProviderStrategy>,
    supportsAutomatedDeductions?: boolean
  ) => Promise<void>;
}

/**
 * Hook to manage payroll provider selection state and actions
 */
export function usePayrollProviderSelection (): UsePayrollProviderSelectionResult {
  const payrollProviderSearchRef = useRef<HTMLInputElement>(null)

  /**
   * Handles provider selection and delegates to the appropriate strategy
   */
  const handleProviderSelection = (
    provider: PayrollProviderConfig,
    providerStrategies: Record<string, ProviderStrategy>,
    supportsAutomatedDeductions = false
  ): Promise<void> => {
    // Get the appropriate strategy for this provider type
    const strategy = providerStrategies[provider.api_connection_method]

    if (strategy) {
      return strategy.startConnection(provider, supportsAutomatedDeductions)
    } else {
      return Promise.resolve()
    }
  }

  return {
    payrollProviderSearchRef,
    handleProviderSelection
  }
}
